// src/network.js

/**
 * formatPhoneNumber: a special Aussie-aware function.
 * - Strips spaces/dashes.
 * - If user typed (or iOS autofilled) a 9-digit number starting with '4', 
 *   we assume it dropped the leading '0' for "04..." so we restore it.
 * - Then if it starts with '0', convert to +61.
 * - Otherwise if it doesn't start with '+', just add '+' in front.
 */
import { CONFIG } from "./config";

function formatPhoneNumber(rawPhone) {
    let formatted = rawPhone.trim().replace(/\s+/g, '').replace(/-/g, '');
  
    // [1] If it doesn't start with '0' or '+', but it looks like 
    //     an Aussie mobile missing the leading '0' (e.g. '476374662'):
    //     we restore '0' at the front.
    //     (Typical Aussie mobile is 10 digits starting with '04'.)
    if (!formatted.startsWith('0') && !formatted.startsWith('+')) {
      // If it has 9 digits and starts with '4', assume missing '0'
      if (formatted.length === 9 && formatted.startsWith('4')) {
        formatted = '0' + formatted; // e.g. '476374662' => '0476374662'
      }
    }
  
    // [2] Now if it starts with '0', rewrite to +61
    if (formatted.startsWith('0')) {
      // e.g. '0476374662' => '+61476374662'
      formatted = '+61' + formatted.slice(1);
    }
    // [3] Else if it doesn't start with '+', prepend '+'
    else if (!formatted.startsWith('+')) {
      // e.g. '61476374662' => '+61476374662'
      formatted = '+' + formatted;
    }
  
    return formatted;
  }
  
  /**
   * networkRequest: low-level function that sends the request to your backend,
   * encoding the JSON in base64 and sending it as multipart/form-data.
   */
  async function networkRequest(cmd, lockID, name = '', phone = '', twoFa = '') {
    // Use the dynamic API URL
    const url = CONFIG.apiUrl;
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  
    // Always do final phone formatting here:
    const finalPhone = formatPhoneNumber(phone);
  
    const bodyDict = {
      lock_id: lockID,
      name,
      phone: finalPhone,
      tz: timeZone,
      two_fa: twoFa.trim(),
      cmd,
    };
  
    const jsonData = JSON.stringify(bodyDict);
    const base64Body = btoa(jsonData);
  
    const boundary = 'Boundary-' + Math.random().toString(16).slice(2);
    const multipartBody =
      `--${boundary}\r\n` +
      `Content-Disposition: form-data; name="b64"\r\n\r\n` +
      `${base64Body}\r\n` +
      `--${boundary}--\r\n`;
  
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': `multipart/form-data; boundary=${boundary}`,
      },
      body: multipartBody,
    });
  
    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }
  
    const json = await response.json();

  
    if (json.data) {
      if (json.data.hasOwnProperty('auth')) {
        const val = json.data.auth;
        json.data.auth = val === true || val === 'true' || val === 1 || val === '1';
      } else if (json.data.hasOwnProperty('unlocked')) {
        const val = json.data.unlocked;
        json.data.auth = val === true || val === 'true' || val === 1 || val === '1';
      }
    }
  
    return json;
  }
  
  // High-level calls:
  export async function getSite(lockID) {
    return networkRequest('get-site-app-clip-instant-app', lockID);
  }

  export async function checkUserAuth(lockID, name, phone, twoFa) {
    return networkRequest('authenticate-check', lockID, name, phone, twoFa);
  }
  
  export async function confirmVerificationCode(lockID, name, phone, twoFa) {
    return networkRequest('compare-2FA', lockID, name, phone, twoFa);
  }
  
  export async function unlockDoor(lockID, name, phone, twoFa) {
    return networkRequest('unlock-after-2FA', lockID, name, phone, twoFa);
  }

  export async function unlockAgain(lockID, name, phone) {
    return networkRequest('authenticate-unlock-door', lockID, name, phone);
  }
  
  